import { useState, useCallback, useEffect, useRef } from 'react';
import { runRep, retrySaveProgress } from '@services/repService';
import { refreshProgress } from '@hooks/useProgress';

/**
 * The sandbox's explicit state machine (spec Part 8) — "implement this
 * explicitly, do not infer state from button labels." `phase` is the one
 * source of truth every work surface and the shell derive editor-lock,
 * submit-label, and rail-expansion from.
 *
 * `ready` isn't a phase stored here — it's `phase === 'idle' && canSubmit`,
 * computed by the caller, since "has valid content" is format-specific
 * (a non-empty textarea vs. every MCQ answered vs. both compare prompts +
 * a selection) and this hook has no opinion on what counts as valid.
 *
 * A pass and the saving of that pass are separate facts: the run result carries
 * `saved` (true | false | null). `phase` stays 'pass' either way; `save`
 * (returned below) says whether the progress write landed and retries it.
 *
 * `error` is a distinct phase from `fail` — a system/network failure is
 * never a scored fail (Part 5): it doesn't touch failCount, and the UI
 * must not present it as a learning outcome.
 */
export const useRunState = (repId) => {
  const [phase, setPhase] = useState('idle'); // idle | submitted | waiting | pass | fail | error
  const [traceEvents, setTraceEvents] = useState([]);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [failCount, setFailCount] = useState(0);
  const [lastSubmission, setLastSubmission] = useState(null);
  // Save of a PASS: null (nothing to save) | 'saved' | 'unsaved' | 'saving'
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [savedByRetry, setSavedByRetry] = useState(false);
  const runIdRef = useRef(0);

  const clearSave = () => { setSaveStatus(null); setSaveError(null); setSavedByRetry(false); };

  // A different rep is a clean slate — including failCount, which is
  // explicitly "on the same rep" (Part 7's escalation rule).
  useEffect(() => {
    runIdRef.current += 1;
    setPhase('idle');
    setTraceEvents([]);
    setResult(null);
    setErrorMessage(null);
    setFailCount(0);
    setLastSubmission(null);
    setSaveStatus(null);
    setSaveError(null);
    setSavedByRetry(false);
  }, [repId]);

  const submit = useCallback(async (submission) => {
    const thisRun = ++runIdRef.current;
    const isStale = () => thisRun !== runIdRef.current;

    setPhase('submitted');
    setTraceEvents([]);
    setResult(null);
    setErrorMessage(null);
    setLastSubmission(submission);
    clearSave();

    try {
      await runRep({
        repId,
        submission,
        onEvent: (evt) => {
          if (isStale()) return;
          if (evt.type === 'stage') {
            setPhase(p => (p === 'submitted' ? 'waiting' : p));
            setTraceEvents(prev => [...prev, evt]);
          } else if (evt.type === 'result') {
            setResult(evt.result);
            if (evt.result.passed) {
              setPhase('pass');
              setFailCount(0);
              if (evt.result.saved === false) {
                setSaveStatus('unsaved');
              } else if (evt.result.saved === true) {
                setSaveStatus('saved');
                refreshProgress(); // the map / lesson counts update without a page refresh
              }
            } else {
              setPhase('fail');
              setFailCount(c => c + 1);
            }
          } else if (evt.type === 'error') {
            setErrorMessage(evt.message);
            setPhase('error');
          }
        },
      });
    } catch (err) {
      if (isStale()) return;
      const unreachable = err instanceof TypeError;
      setErrorMessage(unreachable
        ? "Can't reach the server. Check your connection and try again."
        : (err.message || 'Something went wrong. Please try again.'));
      setPhase('error');
    }
  }, [repId]);

  const retry = useCallback(() => {
    if (lastSubmission != null) submit(lastSubmission);
  }, [submit, lastSubmission]);

  // Re-sends the SAVE of the passing attempt — never a re-score. The server
  // saves the score it recorded for result.executionId.
  const executionId = result?.executionId ?? null;
  const retrySave = useCallback(async () => {
    if (executionId == null) return;
    const thisRun = runIdRef.current;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      await retrySaveProgress({ repId, executionId });
      if (thisRun !== runIdRef.current) return;
      setSaveStatus('saved');
      setSavedByRetry(true);
      refreshProgress();
    } catch (err) {
      if (thisRun !== runIdRef.current) return;
      setSaveStatus('unsaved');
      setSaveError(err instanceof TypeError
        ? "Can't reach the server. Check your connection and try again."
        : (err.message || "Couldn't save. Please try again."));
    }
  }, [repId, executionId]);

  return {
    phase,
    traceEvents,
    result,
    errorMessage,
    failCount,
    // Part 7: reveals the moment the SECOND failure lands on the SAME rep
    // (spec: "after two failures") — showCriteria flips true as soon as
    // failCount hits 2, so the reveal appears alongside that 2nd attempt's
    // own result, not deferred to a 3rd attempt.
    showCriteria: failCount >= 2,
    isRunning: phase === 'submitted' || phase === 'waiting',
    isLocked: phase === 'submitted' || phase === 'waiting' || phase === 'pass',
    // Both leave the editor unlocked and the text in place, but only a
    // scored fail gets the amber "you're revising a real attempt"
    // treatment — a system error isn't a learning outcome (Part 5) and
    // must not look like one.
    isFailed: phase === 'fail',
    isSystemError: phase === 'error',
    submit,
    retry,
    save: {
      status: saveStatus,
      error: saveError,
      canRetry: executionId != null,
      recovered: savedByRetry,
      retry: retrySave,
    },
  };
};

/** Submit-button label for the current phase — never inferred elsewhere. */
export const submitLabel = (phase, baseLabel) => {
  if (phase === 'submitted' || phase === 'waiting') return 'evaluating…';
  if (phase === 'pass') return '$ continue';
  if (phase === 'fail') return '$ revise and run';
  // A system error asks nothing of the learner's text — "try again", not
  // "revise", since nothing about their submission was actually wrong.
  if (phase === 'error') return '$ try again';
  return baseLabel;
};

export default useRunState;
